import { Router } from "@angular/router";
import moment from "moment";
import { BehaviorSubject } from "rxjs";
import { encodedDepartmentsToSelection } from "src/app/common/departmentFilterUtils";
import { AutocompleteSearchResultsModel } from "src/app/models/api/autocompleteSearchResults.model";
import { ResourceTypesPerPlatform } from "src/app/models/api/common";
import { PolicyMinimalModel } from "src/app/models/api/governance/policy.model";
import { AttackSelectOptions } from "src/app/models/selectOptions/attackSelectOptions.model";
import { OwaspSelectOptions } from "src/app/models/selectOptions/owaspSelectOptions.model";
import { ResourceStatusSelectOptions } from "src/app/models/selectOptions/resourceStatusSelectOptions.model";
import { ResourceTypeSelectOptions } from "src/app/models/selectOptions/resourceTypeSelectOptions.model";
import { ViolationStatusesSelectOptions } from "src/app/models/selectOptions/violationStatusesSelectOptions.model";
import { SortColumn } from "src/app/models/sortColumn.model";
import { ViolationsTableColumnsModel } from "src/app/models/tableColumns/violationsTableColumn.model";
import { FiltersQueryTransformer } from "src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer";
import { ViolationRulesHttpService } from "src/app/services/httpServices/governance/violationRulesHttpService/violation-rules-http.service";
import { InventoryHttpService } from "src/app/services/httpServices/inventoryHttpService/inventory-http.service";
import { StoreService } from "src/app/services/infra/store/store.service";
import { IntegrationModel } from "../../../api";
import { ISelectOption, PlatformSelectOptions, SeveritySelectOptions, ViolationCategorySelectOptions } from "../../../selectOptions";
import { FilterTypesEnum } from "../../filterTypes.enum";
import { IPageFilters } from "../../interfaces/pageFilters.interface";
import { ISelectedFilters } from "../../selectedFilters.interface";
import { DateFilter } from "../filtersTypes/dateFilter.model";
import { DynamicMultiSelectFilters } from "../filtersTypes/dynamicFilter.model";
import { MultiSelectFilters } from "../filtersTypes/multiFilter.model";
import { TextFilter } from "../filtersTypes/textFilter.model";
import {
    checkAllGivenIdsExistInOptions,
    fromAutocompleteSearchResultToSelectOption,
    fromIntegrationToSelectOption,
    fromLabelToSelectOption,
    fromPolicyMinimalToSelectOption,
    fromViolationRuleToSelectOption,
    removeUnusedFilterByFeatureFlag,
} from "./utils";
import { GenerativeAiOptionsSelectOptions } from "src/app/models/selectOptions/generativeAiOptionsSelectOptions.model";
import { FeatureFlagTypes } from "src/app/models/featureFlag/featureFalgsTypes.enum";
import { FeatureFlagsService } from "src/app/services/infra/featureFlags/feature-flags.service";
import { RuleTagSelectOptions } from "src/app/models/selectOptions/ruleTagsSelectOptions.model";
import { ViolationStatusReasonSelectOptions } from "src/app/models/selectOptions/violationsStatusReasonSelectOptions.model";
import { OwaspLlmSelectOptions } from "src/app/models/selectOptions/owaspLlmSelectOptions.model";

export class ViolationsFiltersModel implements IPageFilters {
    public onUpdate$: BehaviorSubject<ISelectedFilters>;
    public allOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

    public categoryFilters: MultiSelectFilters;
    public platformFilters: MultiSelectFilters;
    public resourceStatusFilters: MultiSelectFilters;
    public severityFilters: MultiSelectFilters;
    public integrationFilters: MultiSelectFilters;
    public policyFilters: MultiSelectFilters;
    public violationStatusFilters: MultiSelectFilters;
    public violationStatusReasonFilters: MultiSelectFilters;
    public typeFilters: MultiSelectFilters;
    public owaspFilters: MultiSelectFilters;
    public owaspLlmFilters: MultiSelectFilters;
    public attackFilters: MultiSelectFilters;
    public generativeAiOptionsFilters: MultiSelectFilters;
    public violationRuleFilters: MultiSelectFilters;
    public fromDate: DateFilter;
    public toDate: DateFilter;
    public search: TextFilter;
    public sortedColumn$: BehaviorSubject<SortColumn | undefined> = new BehaviorSubject<SortColumn | undefined>(undefined);
    public environmentFilters: DynamicMultiSelectFilters;
    public createdByFilters: DynamicMultiSelectFilters;
    public ownedByFilters: DynamicMultiSelectFilters;
    public departmentFilters: DynamicMultiSelectFilters;
    public labelFilters: DynamicMultiSelectFilters;
    public ruleTagFilters: MultiSelectFilters;
    public featureFlagTypes = FeatureFlagTypes;
    private _defaultSortColumn: SortColumn | undefined = undefined;

    /**
     *
     */
    constructor(
        private _router: Router,
        private _store: StoreService,
        private _violationRulesHttpService: ViolationRulesHttpService,
        private _inventoryHttpService: InventoryHttpService,
        public featureFlagsService: FeatureFlagsService,
    ) {
        this._initializeFilters();
        this._integrationsOnUpdate();
        this._policyOnUpdate();
        this._getViolationRules();
        this._getLabels();
        this.onUpdate$ = new BehaviorSubject(this.getSelectedFilters());

        this._initDefaultSortColumn();
    }

    public getAllFiltersOptions(): BehaviorSubject<ISelectOption[]> {
        return this.allOptions$;
    }

    public getAllSelectedOptions(): ISelectOption[] {
        const selectedOptions = [
            ...this.categoryFilters.getSelectedOptions(),
            ...this.typeFilters.getSelectedOptions(),
            ...this.integrationFilters.getSelectedOptions(),
            ...this.policyFilters.getSelectedOptions(),
            ...this.violationStatusFilters.getSelectedOptions(),
            ...this.violationStatusReasonFilters.getSelectedOptions(),
            ...this.platformFilters.getSelectedOptions(),
            ...this.severityFilters.getSelectedOptions(),
            ...this.resourceStatusFilters.getSelectedOptions(),
            ...this.owaspFilters.getSelectedOptions(),
            ...this.owaspLlmFilters.getSelectedOptions(),
            ...this.attackFilters.getSelectedOptions(),
            ...this.generativeAiOptionsFilters.getSelectedOptions(),
            ...this.violationRuleFilters.getSelectedOptions(),
            ...this.environmentFilters.getSelectedOptions(),
            ...this.createdByFilters.getSelectedOptions(),
            ...this.ownedByFilters.getSelectedOptions(),
            ...this.departmentFilters.getSelectedOptions(),
            ...this.labelFilters.getSelectedOptions(),
            ...this.ruleTagFilters.getSelectedOptions(),
        ];

        return selectedOptions;
    }

    public getSelectedFilters(): ISelectedFilters {
        return {
            category: this.categoryFilters.getSelectedFiltersValues(),
            platform: this.platformFilters.getSelectedFiltersValues(),
            resourceStatus: this.resourceStatusFilters.getSelectedFiltersValues(),
            severity: this.severityFilters.getSelectedFiltersValues(),
            violationStatus: this.violationStatusFilters.getSelectedFiltersValues(),
            violationStatusReason: this.violationStatusReasonFilters.getSelectedFiltersValues(),
            type: this.typeFilters.getSelectedFiltersValues(),
            owasp: this.owaspFilters.getSelectedFiltersValues().concat(this.owaspLlmFilters.getSelectedFiltersValues()),
            owaspLlm: this.owaspLlmFilters.getSelectedFiltersValues(),
            attack: this.attackFilters.getSelectedFiltersValues(),
            generativeAiOptions: this.generativeAiOptionsFilters.getSelectedFiltersValues(),
            violationRule: this.violationRuleFilters.getSelectedFiltersValues(),
            policy: this.policyFilters.getSelectedFiltersValues(),
            integration: this.integrationFilters.getSelectedFiltersValues(),
            fromDate: this.fromDate.getFilterValue(),
            toDate: this.toDate.getFilterValue(),
            search: this.search.getFilterValue(),
            environment: this.environmentFilters.getSelectedFiltersValues(),
            createdBy: this.createdByFilters.getSelectedFiltersValues(),
            ownedBy: this.ownedByFilters.getSelectedFiltersValues(),
            department: this.departmentFilters.getSelectedFiltersValues(),
            label: this.labelFilters.getSelectedFiltersValues(),
            ruleTag: this.ruleTagFilters.getSelectedFiltersValues(),
        } as ISelectedFilters;
    }

    public getDynamicFilters(): string[] {
        return [FilterTypesEnum.Environment, FilterTypesEnum.CreatedBy, FilterTypesEnum.OwnedBy, FilterTypesEnum.Department, FilterTypesEnum.Label];
    }

    public getNewFilters(): string[] {
        return [FilterTypesEnum.GenerativeAiOptions];
    }

    // * IMPORTANT - Options that are not shown in the filters do not need to be updated
    public updateFilterFromUserSelection(filterToUpdate: FilterTypesEnum | undefined, value: ISelectOption[] | Date | boolean | string | undefined) {
        let needUpdate = false;

        if (filterToUpdate) {
            switch (filterToUpdate) {
                case FilterTypesEnum.FromDate:
                    this.fromDate.update(value as Date | undefined);
                    needUpdate = true;
                    break;
                case FilterTypesEnum.ToDate:
                    this.toDate.update(value as Date | undefined);
                    needUpdate = true;
                    break;
                case FilterTypesEnum.Search:
                    const foundOption = this.violationRuleFilters.getOptions$().value.find((option) => option.id == value);

                    if (foundOption) {
                        const selectedRules = this.violationRuleFilters.getSelectedOptions();
                        selectedRules.push(foundOption);
                        needUpdate = this.violationRuleFilters.updateByValuesIfNeeded(selectedRules.map((option) => option.id));
                        if (needUpdate) {
                            this.search.update(undefined);
                        }
                    } else {
                        this.search.update(value as string | undefined);
                        needUpdate = true;
                    }
                    break;
                default:
                    break;
            }
        } else {
            if (value instanceof Array) {
                if (value.length == 0) {
                    needUpdate = this._clearAllMultiSelectFiltersIfNeeded();
                } else {
                    const optionsByCategory: {
                        [key: string]: ISelectOption[];
                    } = {};
                    value.forEach((option) => {
                        if (option.filterType) {
                            if (option.filterType in optionsByCategory) {
                                optionsByCategory[option.filterType].push(option);
                            } else {
                                optionsByCategory[option.filterType] = [option];
                            }
                        }
                    });
                    this._updateAllOptions();
                    needUpdate = this.platformFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Platform] || []) || needUpdate;
                    needUpdate = this.resourceStatusFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.ResourceStatus] || []) || needUpdate;
                    needUpdate = this.severityFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Severity] || []) || needUpdate;
                    needUpdate = this.typeFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.ResourceType] || []) || needUpdate;
                    needUpdate = this.integrationFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Integration] || []) || needUpdate;
                    needUpdate = this.policyFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Policy] || []) || needUpdate;
                    needUpdate = this.categoryFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.ViolationCategory] || []) || needUpdate;
                    needUpdate = this.violationStatusFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.ViolationStatus] || []) || needUpdate;
                    needUpdate =
                        this.violationStatusReasonFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.ViolationStatusReason] || []) ||
                        needUpdate;
                    needUpdate = this.owaspFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Owasp] || []) || needUpdate;
                    needUpdate = this.owaspLlmFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.OwaspLlm] || []) || needUpdate;
                    needUpdate = this.attackFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Attack] || []) || needUpdate;
                    needUpdate =
                        this.generativeAiOptionsFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.GenerativeAiOptions] || []) || needUpdate;
                    needUpdate = this.environmentFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Environment] || []) || needUpdate;
                    needUpdate = this.createdByFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.CreatedBy] || []) || needUpdate;
                    needUpdate = this.ownedByFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.OwnedBy] || []) || needUpdate;
                    needUpdate = this.departmentFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Department] || []) || needUpdate;
                    needUpdate = this.labelFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Label] || []) || needUpdate;
                    needUpdate = this.ruleTagFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.RuleTag] || []) || needUpdate;
                }
            }
        }
        if (needUpdate) {
            this._handleSortedColumnOnFilter(filterToUpdate);
            const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
            this._router.navigate([], {
                queryParams: { q: filtersQuery },
                queryParamsHandling: "merge",
            });
            this.onUpdate$.next(this.getSelectedFilters());
        }
    }

    public removeSelectedOptionsIfExist(options: ISelectOption[]): void {
        const optionsIdsByCategory: { [key: string]: Set<string> } = {};
        options.forEach(
            (option) => {
                if (option.filterType) {
                    if (option.filterType in optionsIdsByCategory) {
                        optionsIdsByCategory[option.filterType].add(option.id);
                    } else {
                        optionsIdsByCategory[option.filterType] = new Set([option.id]);
                    }
                }
            },
            this.getDynamicFilters().forEach((filter) => {
                optionsIdsByCategory[filter] = new Set();
            }),
        );

        if (optionsIdsByCategory[FilterTypesEnum.Platform]) {
            this.platformFilters.updateIfNeeded(
                this.platformFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.Platform] && !optionsIdsByCategory[FilterTypesEnum.Platform].has(selectedOption.id),
                    ),
            );
        }

        if (optionsIdsByCategory[FilterTypesEnum.Integration]) {
            this.integrationFilters.updateIfNeeded(
                this.integrationFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.Integration] &&
                            !optionsIdsByCategory[FilterTypesEnum.Integration].has(selectedOption.id),
                    ),
            );
        }

        if (optionsIdsByCategory[FilterTypesEnum.Policy]) {
            this.policyFilters.updateIfNeeded(
                this.policyFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.Policy] && !optionsIdsByCategory[FilterTypesEnum.Policy].has(selectedOption.id),
                    ),
            );
        }

        if (optionsIdsByCategory[FilterTypesEnum.ViolationCategory]) {
            this.categoryFilters.updateIfNeeded(
                this.categoryFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.ViolationCategory] &&
                            !optionsIdsByCategory[FilterTypesEnum.ViolationCategory].has(selectedOption.id),
                    ),
            );
        }

        if (optionsIdsByCategory[FilterTypesEnum.ResourceType]) {
            this.typeFilters.updateIfNeeded(
                this.typeFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.ResourceType] &&
                            !optionsIdsByCategory[FilterTypesEnum.ResourceType].has(selectedOption.id),
                    ),
            );
        }

        if (optionsIdsByCategory[FilterTypesEnum.ResourceStatus]) {
            this.resourceStatusFilters.updateIfNeeded(
                this.resourceStatusFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.ResourceStatus] &&
                            !optionsIdsByCategory[FilterTypesEnum.ResourceStatus].has(selectedOption.id),
                    ),
            );
        }

        if (optionsIdsByCategory[FilterTypesEnum.Owasp]) {
            this.owaspFilters.updateIfNeeded(
                this.owaspFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.Owasp] && !optionsIdsByCategory[FilterTypesEnum.Owasp].has(selectedOption.id),
                    ),
            );
        }

        if (optionsIdsByCategory[FilterTypesEnum.OwaspLlm]) {
            this.owaspLlmFilters.updateIfNeeded(
                this.owaspLlmFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.OwaspLlm] && !optionsIdsByCategory[FilterTypesEnum.OwaspLlm].has(selectedOption.id),
                    ),
            );
        }

        if (optionsIdsByCategory[FilterTypesEnum.Attack]) {
            this.attackFilters.updateIfNeeded(
                this.attackFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.Attack] && !optionsIdsByCategory[FilterTypesEnum.Attack].has(selectedOption.id),
                    ),
            );
        }

        if (optionsIdsByCategory[FilterTypesEnum.GenerativeAiOptions]) {
            this.generativeAiOptionsFilters.updateIfNeeded(
                this.generativeAiOptionsFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.GenerativeAiOptions] &&
                            !optionsIdsByCategory[FilterTypesEnum.GenerativeAiOptions].has(selectedOption.id),
                    ),
            );
        }

        if (optionsIdsByCategory[FilterTypesEnum.ViolationRule]) {
            this.violationRuleFilters.updateIfNeeded(
                this.violationRuleFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.ViolationRule] &&
                            !optionsIdsByCategory[FilterTypesEnum.ViolationRule].has(selectedOption.id),
                    ),
            );
        }

        if (optionsIdsByCategory[FilterTypesEnum.ViolationStatus]) {
            this.violationStatusFilters.updateIfNeeded(
                this.violationStatusFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.ViolationStatus] &&
                            !optionsIdsByCategory[FilterTypesEnum.ViolationStatus].has(selectedOption.id),
                    ),
            );
        }

        if (optionsIdsByCategory[FilterTypesEnum.ViolationStatusReason]) {
            this.violationStatusReasonFilters.updateIfNeeded(
                this.violationStatusReasonFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.ViolationStatusReason] &&
                            !optionsIdsByCategory[FilterTypesEnum.ViolationStatusReason].has(selectedOption.id),
                    ),
            );
        }

        if (optionsIdsByCategory[FilterTypesEnum.Severity]) {
            this.severityFilters.updateIfNeeded(
                this.severityFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.CreationTimeSinceDate] &&
                            !optionsIdsByCategory[FilterTypesEnum.CreationTimeSinceDate].has(selectedOption.id),
                    ),
            );
        }

        if (optionsIdsByCategory[FilterTypesEnum.Environment]) {
            this.environmentFilters.updateIfNeeded(
                this.environmentFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.Environment] &&
                            !optionsIdsByCategory[FilterTypesEnum.Environment].has(selectedOption.id),
                    ),
            );
        }
        if (optionsIdsByCategory[FilterTypesEnum.CreatedBy]) {
            this.createdByFilters.updateIfNeeded(
                this.createdByFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.CreatedBy] &&
                            !optionsIdsByCategory[FilterTypesEnum.CreatedBy].has(selectedOption.id),
                    ),
            );
        }
        if (optionsIdsByCategory[FilterTypesEnum.OwnedBy]) {
            this.ownedByFilters.updateIfNeeded(
                this.ownedByFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.OwnedBy] && !optionsIdsByCategory[FilterTypesEnum.OwnedBy].has(selectedOption.id),
                    ),
            );
        }
        if (optionsIdsByCategory[FilterTypesEnum.Department]) {
            this.departmentFilters.updateIfNeeded(
                this.departmentFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.Department] &&
                            !optionsIdsByCategory[FilterTypesEnum.Department].has(selectedOption.id),
                    ),
            );
        }
        if (optionsIdsByCategory[FilterTypesEnum.Label]) {
            this.labelFilters.updateIfNeeded(
                this.labelFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.Label] && !optionsIdsByCategory[FilterTypesEnum.Label].has(selectedOption.id),
                    ),
            );
        }
        if (optionsIdsByCategory[FilterTypesEnum.RuleTag]) {
            this.ruleTagFilters.updateIfNeeded(
                this.ruleTagFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.RuleTag] && !optionsIdsByCategory[FilterTypesEnum.RuleTag].has(selectedOption.id),
                    ),
            );
        }

        const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
        this._router.navigate([], {
            queryParams: { q: filtersQuery },
            queryParamsHandling: "merge",
        });
        this.onUpdate$.next(this.getSelectedFilters());
    }

    public getFilterOptionsIfExist$(filter: FilterTypesEnum): BehaviorSubject<ISelectOption[]> {
        switch (filter) {
            case FilterTypesEnum.ResourceCategory:
                return this.categoryFilters.getOptions$();

            case FilterTypesEnum.Platform:
                return this.platformFilters.getOptions$();

            case FilterTypesEnum.ViolationStatus:
                return this.violationStatusFilters.getOptions$();

            case FilterTypesEnum.ViolationStatusReason:
                return this.violationStatusReasonFilters.getOptions$();

            case FilterTypesEnum.Severity:
                return this.severityFilters.getOptions$();

            case FilterTypesEnum.ResourceType:
                return this.typeFilters.getOptions$();

            case FilterTypesEnum.Integration:
                return this.integrationFilters.getOptions$();

            case FilterTypesEnum.Policy:
                return this.policyFilters.getOptions$();

            case FilterTypesEnum.ViolationCategory:
                return this.categoryFilters.getOptions$();

            case FilterTypesEnum.ResourceStatus:
                return this.resourceStatusFilters.getOptions$();

            case FilterTypesEnum.Owasp:
                return this.owaspFilters.getOptions$();

            case FilterTypesEnum.OwaspLlm:
                return this.owaspLlmFilters.getOptions$();

            case FilterTypesEnum.Attack:
                return this.attackFilters.getOptions$();

            case FilterTypesEnum.GenerativeAiOptions:
                return this.generativeAiOptionsFilters.getOptions$();

            case FilterTypesEnum.ViolationRule:
                return this.violationRuleFilters.getOptions$();

            case FilterTypesEnum.Environment:
                return this.environmentFilters.getOptions$();

            case FilterTypesEnum.CreatedBy:
                return this.createdByFilters.getOptions$();

            case FilterTypesEnum.OwnedBy:
                return this.ownedByFilters.getOptions$();

            case FilterTypesEnum.Department:
                return this.departmentFilters.getOptions$();

            case FilterTypesEnum.Label:
                return this.labelFilters.getOptions$();

            case FilterTypesEnum.RuleTag:
                return this.ruleTagFilters.getOptions$();

            default:
                throw new Error(`ViolationsFiltersModel getFilterOptionsIfExist: ${filter} not supported`);
        }
    }

    public getFilterSelectedIfExist(filter: FilterTypesEnum): string[] {
        switch (filter) {
            case FilterTypesEnum.Platform:
                return this.platformFilters.getSelectedFiltersIds();

            case FilterTypesEnum.ViolationStatus:
                return this.violationStatusFilters.getSelectedFiltersIds();

            case FilterTypesEnum.ViolationStatusReason:
                return this.violationStatusReasonFilters.getSelectedFiltersIds();

            case FilterTypesEnum.Severity:
                return this.severityFilters.getSelectedFiltersIds();

            case FilterTypesEnum.ResourceType:
                return this.typeFilters.getSelectedFiltersIds();

            case FilterTypesEnum.Integration:
                return this.integrationFilters.getSelectedFiltersIds();

            case FilterTypesEnum.Policy:
                return this.policyFilters.getSelectedFiltersIds();

            case FilterTypesEnum.ViolationCategory:
                return this.categoryFilters.getSelectedFiltersIds();

            case FilterTypesEnum.ResourceStatus:
                return this.resourceStatusFilters.getSelectedFiltersIds();

            case FilterTypesEnum.Owasp:
                return this.owaspFilters.getSelectedFiltersIds();

            case FilterTypesEnum.OwaspLlm:
                return this.owaspLlmFilters.getSelectedFiltersIds();

            case FilterTypesEnum.Attack:
                return this.attackFilters.getSelectedFiltersIds();

            case FilterTypesEnum.GenerativeAiOptions:
                return this.generativeAiOptionsFilters.getSelectedFiltersIds();

            case FilterTypesEnum.ViolationRule:
                return this.violationRuleFilters.getSelectedFiltersIds();

            case FilterTypesEnum.Environment:
                return this.environmentFilters.getSelectedFiltersIds();

            case FilterTypesEnum.CreatedBy:
                return this.createdByFilters.getSelectedFiltersIds();

            case FilterTypesEnum.OwnedBy:
                return this.ownedByFilters.getSelectedFiltersIds();

            case FilterTypesEnum.Department:
                return this.departmentFilters.getSelectedFiltersIds();

            case FilterTypesEnum.Label:
                return this.labelFilters.getSelectedFiltersIds();

            case FilterTypesEnum.RuleTag:
                return this.ruleTagFilters.getSelectedFiltersIds();

            default:
                throw new Error(`ViolationsFiltersModel getFilterSelectedIfExist: ${filter} not supported`);
        }
    }

    public updateFiltersByKeyValue(keyValueFilters: { [key: string]: string[] }, updateQueryParams = false): void {
        let needUpdate = false;

        let values;

        if ("fromDate" in keyValueFilters || this.fromDate.getFilterValue() !== undefined) {
            this.fromDate.update(
                keyValueFilters["fromDate"] && moment(keyValueFilters["fromDate"][0], "x").isValid()
                    ? moment(keyValueFilters["fromDate"][0], "x").toDate()
                    : undefined,
            );
            needUpdate = true;
        }

        if ("toDate" in keyValueFilters || this.toDate.getFilterValue() !== undefined) {
            this.toDate.update(
                keyValueFilters["toDate"] && moment(keyValueFilters["toDate"][0], "x").isValid()
                    ? moment(keyValueFilters["toDate"][0], "x").toDate()
                    : undefined,
            );
            needUpdate = true;
        }

        if ("search" in keyValueFilters || this.search.getFilterValue() !== undefined) {
            this.search.update(keyValueFilters["search"] ? keyValueFilters["search"][0] : undefined);
            needUpdate = true;
        }

        values = "category" in keyValueFilters ? keyValueFilters["category"] : [];
        needUpdate = this.categoryFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "platform" in keyValueFilters ? keyValueFilters["platform"] : [];
        needUpdate = this.platformFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "resourceStatus" in keyValueFilters ? keyValueFilters["resourceStatus"] : [];
        needUpdate = this.resourceStatusFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "severity" in keyValueFilters ? keyValueFilters["severity"] : [];
        needUpdate = this.severityFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "violationStatus" in keyValueFilters ? keyValueFilters["violationStatus"] : [];
        needUpdate = this.violationStatusFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "violationStatusReason" in keyValueFilters ? keyValueFilters["violationStatusReason"] : [];
        needUpdate = this.violationStatusReasonFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "type" in keyValueFilters ? keyValueFilters["type"] : [];
        needUpdate = this.typeFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "integration" in keyValueFilters ? keyValueFilters["integration"] : [];
        needUpdate = this.integrationFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "policy" in keyValueFilters ? keyValueFilters["policy"] : [];
        needUpdate = this.policyFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "owasp" in keyValueFilters ? keyValueFilters["owasp"] : [];
        needUpdate = this.owaspFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "owaspLlm" in keyValueFilters ? keyValueFilters["owaspLlm"] : [];
        needUpdate = this.owaspLlmFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "attack" in keyValueFilters ? keyValueFilters["attack"] : [];
        needUpdate = this.attackFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "generativeAiOptions" in keyValueFilters ? keyValueFilters["generativeAiOptions"] : [];
        needUpdate = this.generativeAiOptionsFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "violationRule" in keyValueFilters ? keyValueFilters["violationRule"] : [];
        needUpdate = this.violationRuleFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "environment" in keyValueFilters ? keyValueFilters["environment"] : [];
        if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.environmentFilters.getOptions$().value, values)) {
            this.environmentFilters = new DynamicMultiSelectFilters([]);
            this._fetchAndUpdateFilterOptions(values, FilterTypesEnum.Environment, "environment", this.environmentFilters);
        }
        needUpdate = this.environmentFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "createdBy" in keyValueFilters ? keyValueFilters["createdBy"] : [];
        if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.createdByFilters.getOptions$().value, values)) {
            this.createdByFilters = new DynamicMultiSelectFilters([]);
            this._fetchAndUpdateFilterOptions(values, FilterTypesEnum.CreatedBy, "user", this.createdByFilters);
        }
        needUpdate = this.createdByFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "ownedBy" in keyValueFilters ? keyValueFilters["ownedBy"] : [];
        if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.ownedByFilters.getOptions$().value, values)) {
            this.ownedByFilters = new DynamicMultiSelectFilters([]);
            this._fetchAndUpdateFilterOptions(values, FilterTypesEnum.OwnedBy, "user", this.ownedByFilters);
        }
        needUpdate = this.ownedByFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "department" in keyValueFilters ? keyValueFilters["department"] : [];
        if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.departmentFilters.getOptions$().value, values)) {
            this.departmentFilters = new DynamicMultiSelectFilters([]);
            this._departmentOnUpdate(values);
        }
        needUpdate = this.departmentFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "label" in keyValueFilters ? keyValueFilters["label"] : [];
        needUpdate = this.labelFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "ruleTag" in keyValueFilters ? keyValueFilters["ruleTag"] : [];
        needUpdate = this.ruleTagFilters.updateByValuesIfNeeded(values) || needUpdate;

        if (needUpdate) {
            if (updateQueryParams) {
                const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
                this._router.navigate([], {
                    queryParams: { q: filtersQuery },
                    queryParamsHandling: "merge",
                });
            }
            this.onUpdate$.next(this.getSelectedFilters());
        }
    }

    public restoreSortColumnToDefaultIfNeeded() {
        if (!this.search.getFilterValue()) {
            this.sortedColumn$.next(this._defaultSortColumn);
        }
    }

    private _clearAllMultiSelectFiltersIfNeeded(): boolean {
        const needUpdate = false;

        return (
            needUpdate ||
            this.platformFilters.updateIfNeeded([]) ||
            this.resourceStatusFilters.updateIfNeeded([]) ||
            this.severityFilters.updateIfNeeded([]) ||
            this.integrationFilters.updateIfNeeded([]) ||
            this.policyFilters.updateIfNeeded([]) ||
            this.typeFilters.updateIfNeeded([]) ||
            this.categoryFilters.updateIfNeeded([]) ||
            this.violationStatusFilters.updateIfNeeded([]) ||
            this.violationStatusFilters.updateByValuesIfNeeded([]) ||
            this.owaspFilters.updateIfNeeded([]) ||
            this.owaspLlmFilters.updateIfNeeded([]) ||
            this.attackFilters.updateIfNeeded([]) ||
            this.generativeAiOptionsFilters.updateIfNeeded([]) ||
            this.violationRuleFilters.updateIfNeeded([]) ||
            this.environmentFilters.updateIfNeeded([]) ||
            this.createdByFilters.updateIfNeeded([]) ||
            this.ownedByFilters.updateIfNeeded([]) ||
            this.departmentFilters.updateIfNeeded([]) ||
            this.labelFilters.updateIfNeeded([]) ||
            this.ruleTagFilters.updateIfNeeded([])
        );
    }

    private _initializeFilters() {
        const filteredResourceTypeSelectOptions = removeUnusedFilterByFeatureFlag(ResourceTypeSelectOptions, this.featureFlagsService);

        this.categoryFilters = new MultiSelectFilters(ViolationCategorySelectOptions);
        this.platformFilters = new MultiSelectFilters(PlatformSelectOptions);
        this.resourceStatusFilters = new MultiSelectFilters(ResourceStatusSelectOptions);
        this.typeFilters = new MultiSelectFilters(filteredResourceTypeSelectOptions);
        this.severityFilters = new MultiSelectFilters(SeveritySelectOptions);
        this.violationStatusFilters = new MultiSelectFilters(ViolationStatusesSelectOptions);
        this.violationStatusReasonFilters = new MultiSelectFilters(ViolationStatusReasonSelectOptions);
        this.owaspFilters = new MultiSelectFilters(OwaspSelectOptions);
        this.owaspLlmFilters = new MultiSelectFilters(OwaspLlmSelectOptions);
        this.attackFilters = new MultiSelectFilters(AttackSelectOptions);
        this.generativeAiOptionsFilters = new MultiSelectFilters(GenerativeAiOptionsSelectOptions);
        this.violationRuleFilters = new MultiSelectFilters([]);
        this.integrationFilters = new MultiSelectFilters([]);
        this.policyFilters = new MultiSelectFilters([]);
        this.fromDate = new DateFilter();
        this.toDate = new DateFilter();
        this.search = new TextFilter();
        this.environmentFilters = new DynamicMultiSelectFilters([]);
        this.createdByFilters = new DynamicMultiSelectFilters([]);
        this.ownedByFilters = new DynamicMultiSelectFilters([]);
        this.departmentFilters = new DynamicMultiSelectFilters([]);
        this.labelFilters = new DynamicMultiSelectFilters([]);
        this.ruleTagFilters = new MultiSelectFilters(RuleTagSelectOptions);

        this._updateAllOptions();
    }

    private _integrationsOnUpdate() {
        this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
            const integrationsPlatformSet: Set<string> = new Set();

            const integrationsOptionsMap: ISelectOption[] = integrations.map((integration) => {
                integrationsPlatformSet.add(integration.platform);
                return fromIntegrationToSelectOption(integration);
            });

            let shouldUpdateSelectedOptions = this.integrationFilters.updateOptions(Object.values(integrationsOptionsMap));

            const platformOptions = this.platformFilters.getOptions$().value.filter((option) => integrationsPlatformSet.has(option.value));

            const supportedTypesSet = new Set<string>();
            platformOptions.forEach((platformOption) => {
                ResourceTypesPerPlatform[platformOption.id].forEach((resourceType) => {
                    supportedTypesSet.add(resourceType);
                });
            });

            const resourceTypesOptions = this.typeFilters.getOptions$().value.filter((option) => supportedTypesSet.has(option.value));

            shouldUpdateSelectedOptions =
                shouldUpdateSelectedOptions ||
                this.platformFilters.updateOptions(platformOptions) ||
                this.typeFilters.updateOptions(resourceTypesOptions);

            this._updateAllOptions();
            if (shouldUpdateSelectedOptions) {
                this.onUpdate$.next(this.getSelectedFilters());
            }
        });
    }

    private _policyOnUpdate() {
        this._store.accountMinimalPolicies$.subscribe((policies: PolicyMinimalModel[]) => {
            const policyOptionsMap: ISelectOption[] = policies.map((policy) =>
                fromPolicyMinimalToSelectOption(policy, this._store.integrationsNamesById),
            );
            const shouldUpdateSelectedOptions = this.policyFilters.updateOptions(policyOptionsMap);

            this._updateAllOptions();
            if (shouldUpdateSelectedOptions) {
                this.onUpdate$.next(this.getSelectedFilters());
            }
        });
    }

    private _fetchAndUpdateFilterOptions(ids: string[], filterType: FilterTypesEnum, filterName: string, filters: DynamicMultiSelectFilters) {
        this._inventoryHttpService.getDisplayNameById(filterName, ids).subscribe((results: AutocompleteSearchResultsModel[]) => {
            const options: ISelectOption[] = results.map((result) => fromAutocompleteSearchResultToSelectOption(result, filterType));
            const shouldUpdateSelectedOptions = filters.updateOptions(Object.values(options));

            this._updateAllOptions();
            if (shouldUpdateSelectedOptions) {
                this.onUpdate$.next(this.getSelectedFilters());
            }
        });
    }

    private _departmentOnUpdate(encodedDepartmentNames: string[]) {
        const selectedDepartments = encodedDepartmentsToSelection(encodedDepartmentNames);
        const shouldUpdateSelectedOptions = this.departmentFilters.updateOptions(Object.values(selectedDepartments));
        this._updateAllOptions();
        if (shouldUpdateSelectedOptions) this.onUpdate$.next(this.getSelectedFilters());
    }

    private _getViolationRules() {
        this._store.baseViolationRule$.subscribe((defaultRules) => {
            const sortedRules = defaultRules.sort((a, b) => (b.id > a.id ? -1 : 1));
            const violationRulesOptions = sortedRules.map(fromViolationRuleToSelectOption);

            const shouldUpdateSelectedOptions = this.violationRuleFilters.updateOptions(violationRulesOptions);
            this._updateAllOptions();
            if (shouldUpdateSelectedOptions) {
                this.onUpdate$.next(this.getSelectedFilters());
            }
        });
    }

    private _getLabels() {
        this._store.labels$.subscribe((labels) => {
            const sortedLabels = labels.sort((a, b) => (b.id > a.displayName ? -1 : 1));
            const labelOptions = sortedLabels.map(fromLabelToSelectOption);

            const shouldUpdateSelectedOptions = this.labelFilters.updateOptions(labelOptions);
            this._updateAllOptions();
            if (shouldUpdateSelectedOptions) {
                this.onUpdate$.next(this.getSelectedFilters());
            }
        });
    }

    private _updateAllOptions() {
        this.allOptions$.next([
            ...this.severityFilters.getOptions$().value,
            ...this.typeFilters.getOptions$().value,
            ...this.platformFilters.getOptions$().value,
            ...this.resourceStatusFilters.getOptions$().value,
            ...this.violationStatusFilters.getOptions$().value,
            ...this.violationStatusReasonFilters.getOptions$().value,
            ...this.owaspFilters.getOptions$().value,
            ...this.owaspLlmFilters.getOptions$().value,
            ...this.attackFilters.getOptions$().value,
            ...this.generativeAiOptionsFilters.getOptions$().value,
            ...this.categoryFilters.getOptions$().value,
            ...this.integrationFilters.getOptions$().value,
            ...this.policyFilters.getOptions$().value,
            ...this.environmentFilters.getOptions$().value,
            ...this.createdByFilters.getOptions$().value,
            ...this.ownedByFilters.getOptions$().value,
            ...this.departmentFilters.getOptions$().value,
            ...this.labelFilters.getOptions$().value,
            ...this.ruleTagFilters.getOptions$().value,
        ]);
    }

    private _initDefaultSortColumn() {
        const securityRiskColumn = ViolationsTableColumnsModel.find((column) => column.propertyKey === "severity");
        if (securityRiskColumn) {
            this._defaultSortColumn = {
                column: securityRiskColumn,
                isDesc: true,
            };
        }
    }

    private _handleSortedColumnOnFilter(filterToUpdate: FilterTypesEnum | undefined) {
        // When applying search we remove any sorted column in order to display results according to their relevancy to the search term
        if (filterToUpdate === FilterTypesEnum.Search && this.search.getFilterValue()) {
            this.sortedColumn$.next(undefined);
            return;
        }

        // Otherwise, for any other filter, if no column is already sorted restore to default sort column if needed
        if (this.sortedColumn$.value === undefined) {
            this.restoreSortColumnToDefaultIfNeeded();
        }
    }
}
